import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Confirmation dialog for marking a prelead as Qualified or Unqualified.
 * Unqualified requires a reason (sent separately from telecalling notes).
 */
const PreleadQualifyConfirmModal = ({
  open,
  onClose,
  onConfirm,
  message = '',
  kind = 'qualified',
  isSubmitting = false
}) => {
  const [reason, setReason] = useState('');
  const isUnqualified = kind === 'unqualified';

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open, kind]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !isSubmitting) onClose?.();
    };
    if (open) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, isSubmitting]);

  if (!open) return null;

  const handleConfirm = () => {
    if (isUnqualified) {
      const trimmed = reason.trim();
      if (!trimmed) {
        toast.error('Please enter a reason.');
        return;
      }
      onConfirm?.(trimmed);
      return;
    }
    onConfirm?.();
  };

  return (
    <div
      className="fixed inset-0 z-[9100] flex items-center justify-center bg-black/30 px-4 py-10"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation">
      <div
        className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prelead-qualify-confirm-title">
        <div className="border-b border-customBlue/15 bg-[#E9F5F6] px-5 py-3">
          <h2 id="prelead-qualify-confirm-title" className="text-base font-semibold text-customBlue">
            Please confirm
          </h2>
        </div>
        <div className="space-y-4 px-5 py-6">
          <p className="text-center text-[15px] leading-relaxed text-gray-800">{message}</p>
          {isUnqualified && (
            <div className="space-y-1.5">
              <label htmlFor="prelead-unqualify-reason" className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Reason
              </label>
              <textarea
                id="prelead-unqualify-reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Budget too low"
                disabled={isSubmitting}
                className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-customBlue focus:outline-none focus:ring-1 focus:ring-customBlue disabled:opacity-60"
              />
            </div>
          )}
        </div>
        <div className="flex justify-center gap-3 border-t border-gray-100 bg-gray-50/80 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="min-w-[6.5rem] rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50">
            No
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || (isUnqualified && !reason.trim())}
            className="min-w-[6.5rem] rounded-lg bg-customBlue px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50">
            {isSubmitting ? 'Saving…' : 'Yes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreleadQualifyConfirmModal;
